import { a as create$6, b as create$8 } from './form.js';
import { S as StateOptions } from './state-options.js';
import { a as businessClassificationOptions, c as businessServiceReceivedOptions, r as recurringPaymentsOptions, s as seasonalBusinessOptions, b as bankAccountTypeOptions } from './business-form-options.js';
import { E as EntityDocumentType } from './Document.js';

const transformEmptyString = (value) => {
    return value === '' ? null : value;
};
const validateRoutingNumber = (routingNumber) => {
    if (!routingNumber)
        return false;
    const integers = Array.from(String(routingNumber), Number);
    if (integers.length === 9) {
        const checksum = ((3 * (integers[0] + integers[3] + integers[6])) +
            (7 * (integers[1] + integers[4] + integers[7])) +
            (integers[2] + integers[5] + integers[8])) % 10;
        if (checksum === 0)
            return true;
    }
    return false;
};
const phoneRegex = /^\d{10}$/;
const urlRegex = /^(?:http(s)?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/;
const numbersOnlyRegex = /^\d+$/;
const ssnRegex = /^(?!000|666|9\d{2})\d{3}(?!00)\d{2}(?!0000)\d{4}$/;
const poBoxRegex = /P\.?\s?O\.?\s?Box|Postal\sBox/i;

// Common Validations
const emailValidation = create$6()
    .email('Enter valid email')
    .transform(transformEmptyString);
const phoneValidation = create$6()
    .matches(phoneRegex, 'Enter valid phone number')
    .transform(transformEmptyString);
// Core Info Validations
const businessNameValidation = create$6()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters')
    .transform(transformEmptyString);
const doingBusinessAsValidation = create$6()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters')
    .transform(transformEmptyString);
const websiteUrlValidation = create$6()
    .matches(urlRegex, 'Enter valid website url')
    .transform(transformEmptyString);
const businessClassificationValidation = create$6()
    .oneOf(businessClassificationOptions.map((option) => option.value), 'Select business classification')
    .transform(transformEmptyString);
const industryValidation = create$6()
    .min(2, 'Industry must be at least 2 characters')
    .max(50, 'Industry must be less than 50 characters')
    .transform(transformEmptyString);
const taxIdValidation = create$6()
    .matches(numbersOnlyRegex, 'Enter valid tax id, SSN, or EIN')
    .test('not-repeat', 'Enter valid tax id, SSN, or EIN', (value) => {
    return !/^(\d)\1+$/.test(value);
})
    .test('not-seq', 'Enter valid tax id, SSN, or EIN', (value) => {
    return value !== '123456789';
})
    .transform(transformEmptyString);
const dateOfIncorporationValidation = create$6()
    .test('not-future', 'Date of incorporation cannot be in the future', (value) => {
    const inputDate = new Date(value);
    const today = new Date();
    return inputDate <= today;
})
    .transform(transformEmptyString);
// Identity Validations
const identityNameValidation = create$6()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters')
    .transform(transformEmptyString);
const identityTitleValidation = create$6()
    .min(2, 'Title must be at least 2 characters')
    .max(50, 'Title must be less than 50 characters')
    .transform(transformEmptyString);
const dobValidation = (role) => {
    return (create$6()
        .test('min', 'Enter a valid date', (value) => {
        const date = new Date(value);
        const minDate = new Date('1902-01-01');
        return date >= minDate;
    })
        .test('age', `${role} must be at least 18 years old`, (value) => {
        const date = new Date(value);
        const minAgeDate = new Date();
        minAgeDate.setFullYear(minAgeDate.getFullYear() - 18);
        return date <= minAgeDate;
    })
        .transform(transformEmptyString));
};
const ssnValidation = create$6()
    .matches(ssnRegex, 'Enter valid SSN')
    .when('ssn_last4', {
    is: (val) => !val || val.length === 0,
    then: (schema) => schema.required('Enter SSN'),
    otherwise: (schema) => schema.nullable(),
})
    .test('not-repeat', 'Enter valid SSN', (value) => {
    return !/^(\d)\1+$/.test(value);
})
    .test('not-seq', 'Enter valid SSN', (value) => {
    return value !== '123456789';
})
    .transform(transformEmptyString);
// Address Validations
const lineOneValidation = create$6()
    .min(5, 'Address must be at least 5 characters')
    .max(100, 'Address must be less than 100 characters')
    .test('not-po-box', 'A PO Box is not a valid address entry', (value) => {
    return !poBoxRegex.test(value);
})
    .transform(transformEmptyString);
const lineTwoValidation = create$6()
    .max(100, 'Address must be less than 100 characters')
    .test('not-po-box', 'A PO Box is not a valid address entry', (value) => {
    return !poBoxRegex.test(value);
})
    .transform(transformEmptyString);
const cityValidation = create$6()
    .min(2, 'City must be at least 2 characters')
    .max(50, 'City must be less than 50 characters')
    .transform(transformEmptyString);
const stateValidation = create$6()
    .oneOf(StateOptions.map((option) => option.value), 'Select state')
    .transform(transformEmptyString);
const postalValidation = create$6()
    .matches(/^[0-9]{5}$/, 'Enter valid postal code')
    .transform(transformEmptyString);
// Additional Questions Validations
const revenueValidation = create$6()
    .matches(numbersOnlyRegex, 'Enter valid revenue')
    .transform(transformEmptyString);
const paymentVolumeValidation = create$6()
    .matches(numbersOnlyRegex, 'Enter valid payment volume')
    .transform(transformEmptyString);
const whenServiceReceivedValidation = create$6()
    .oneOf(businessServiceReceivedOptions.map((option) => option.value), 'Select when service is received')
    .transform(transformEmptyString);
const recurringPaymentsValidation = create$6()
    .oneOf(recurringPaymentsOptions.map((option) => option.value), 'Select recurring payments')
    .transform(transformEmptyString);
const recurringPaymentsPercentageValidation = create$6()
    .when('business_recurring_payments', {
    is: (val) => val === 'Yes',
    then: (schema) => schema.required('Enter recurring payments percentage'),
    otherwise: (schema) => schema.nullable(),
})
    .transform(transformEmptyString);
const seasonalBusinessValidation = create$6()
    .oneOf(seasonalBusinessOptions.map((option) => option.value), 'Select seasonal business')
    .transform(transformEmptyString);
const otherPaymentDetailsValidation = create$6()
    .transform(transformEmptyString);
// Bank Account Validations
const bankNameValidation = create$6()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters')
    .transform(transformEmptyString);
const nicknameValidation = create$6()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters')
    .transform(transformEmptyString);
const accountTypeValidation = create$6()
    .oneOf(bankAccountTypeOptions.map((option) => option.value), 'Select account type')
    .transform(transformEmptyString);
const accountNumberValidation = create$6()
    .min(4, 'Account number must be at least 4 digits')
    .max(17, 'Account number must be less than 17 digits')
    .matches(numbersOnlyRegex, 'Enter valid account number')
    .test('not-repeat', 'Enter valid account number', (value) => {
    return !/^(\d)\1+$/.test(value);
})
    .transform(transformEmptyString);
const routingNumberValidation = create$6()
    .length(9, 'Routing number must be 9 digits')
    .matches(numbersOnlyRegex, 'Enter valid routing number')
    .test('not-repeat', 'Enter valid routing number', (value) => {
    return !/^(\d)\1+$/.test(value);
})
    .test('valid', 'Enter valid routing number', (value) => {
    return validateRoutingNumber(value);
})
    .transform(transformEmptyString);
// Document Upload Validations
const documentUploadValidation = create$8();
const voidedCheckValidation = (documents, allowOptionalFields) => {
    const existingDoc = documents.some((doc) => doc.document_type === EntityDocumentType.voidedCheck);
    if (existingDoc || allowOptionalFields) {
        return documentUploadValidation.nullable();
    }
    else {
        return documentUploadValidation.required('Please select one or more files');
    }
};
const bankStatementValidation = (documents, allowOptionalFields) => {
    const existingDoc = documents.some((doc) => doc.document_type === EntityDocumentType.bankStatement);
    if (existingDoc || allowOptionalFields) {
        return documentUploadValidation.nullable();
    }
    else {
        return documentUploadValidation.required('Please select one or more files');
    }
};

export { industryValidation as A, taxIdValidation as B, dateOfIncorporationValidation as C, identityTitleValidation as D, dobValidation as E, ssnValidation as F, lineTwoValidation as a, paymentVolumeValidation as b, cityValidation as c, recurringPaymentsValidation as d, recurringPaymentsPercentageValidation as e, seasonalBusinessValidation as f, bankNameValidation as g, accountTypeValidation as h, identityNameValidation as i, accountNumberValidation as j, routingNumberValidation as k, lineOneValidation as l, bankStatementValidation as m, nicknameValidation as n, otherPaymentDetailsValidation as o, postalValidation as p, businessNameValidation as q, revenueValidation as r, stateValidation as s, websiteUrlValidation as t, emailValidation as u, voidedCheckValidation as v, whenServiceReceivedValidation as w, phoneValidation as x, doingBusinessAsValidation as y, businessClassificationValidation as z };
